import React from 'react';
import { Search, Filter, Calendar, X } from 'lucide-react';
import { Button } from '../ui/Button/Button';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { USER_ROLES } from '../../pages/users/UserReportsPage';

/**
 * UserFilters Component
 * 
 * A comprehensive filtering component for user data with search,
 * role, gender, and date range filters.
 */
export function UserFilters({
  searchTerm = '',
  roleFilter = 'all',
  genderFilter = 'all',
  dateRange = 'all',
  onSearchChange,
  onRoleChange,
  onGenderChange,
  onDateRangeChange,
  onClearFilters,
  totalUsers = 0,
  filteredUsers = 0,
  className = ''
}) {
  const hasActiveFilters = searchTerm || roleFilter !== 'all' || genderFilter !== 'all' || dateRange !== 'all';

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: USER_ROLES.ADMIN, label: 'Admin' },
    { value: USER_ROLES.MODERATOR, label: 'Moderator' },
    { value: USER_ROLES.USER, label: 'User' }
  ];

  const genderOptions = [
    { value: 'all', label: 'All Genders' },
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' }
  ];

  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'recent', label: 'Recent (30 days)' },
    { value: 'month', label: 'Last Month' },
    { value: 'quarter', label: 'Last 3 Months' }
  ];

  return (
    <Card className={`p-6 ${className}`}>
      {/* Main Filters Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
        {/* Search Input */}
        <div className="lg:col-span-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search users, emails, phone numbers..."
              value={searchTerm}
              onChange={(e) => onSearchChange?.(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            />
            {searchTerm && (
              <button
                onClick={() => onSearchChange?.('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Role Filter */}
        <div>
          <select
            value={roleFilter}
            onChange={(e) => onRoleChange?.(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
          >
            {roleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Gender Filter */}
        <div>
          <select
            value={genderFilter}
            onChange={(e) => onGenderChange?.(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
          >
            {genderOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Date Range Filter */}
        <div>
          <select
            value={dateRange}
            onChange={(e) => onDateRangeChange?.(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
          >
            {dateRangeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Filter Summary and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {/* Results Count */}
          <div className="text-sm text-gray-600">
            Showing <span className="font-medium">{filteredUsers.toLocaleString()}</span> of{' '}
            <span className="font-medium">{totalUsers.toLocaleString()}</span> users
          </div>

          {/* Active Filters Badges */}
          {hasActiveFilters && (
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500">Filters:</span>
              {searchTerm && (
                <Badge variant="info" size="sm">
                  Search: "{searchTerm.length > 20 ? searchTerm.substring(0, 20) + '...' : searchTerm}"
                </Badge>
              )}
              {roleFilter !== 'all' && (
                <Badge variant="info" size="sm">
                  Role: {roleOptions.find(opt => opt.value === roleFilter)?.label}
                </Badge>
              )}
              {genderFilter !== 'all' && (
                <Badge variant="info" size="sm">
                  Gender: {genderOptions.find(opt => opt.value === genderFilter)?.label}
                </Badge>
              )}
              {dateRange !== 'all' && (
                <Badge variant="info" size="sm">
                  Date: {dateRangeOptions.find(opt => opt.value === dateRange)?.label}
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClearFilters}
            className="flex items-center space-x-1"
          >
            <X className="w-4 h-4" />
            <span>Clear Filters</span>
          </Button>
        )}
      </div>
    </Card>
  );
}

/**
 * Compact version of UserFilters for smaller spaces
 */
export function UserFiltersCompact({
  searchTerm = '',
  roleFilter = 'all',
  onSearchChange,
  onRoleChange,
  filteredUsers = 0,
  className = ''
}) {
  return (
    <div className={`flex items-center space-x-4 ${className}`}>
      {/* Search */}
      <div className="flex-1 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => onSearchChange?.(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Role Filter */}
      <div className="w-40">
        <select
          value={roleFilter}
          onChange={(e) => onRoleChange?.(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Roles</option>
          <option value={USER_ROLES.ADMIN}>Admin</option>
          <option value={USER_ROLES.MODERATOR}>Moderator</option>
          <option value={USER_ROLES.USER}>User</option>
        </select>
      </div>

      {/* Results Count */}
      <div className="text-sm text-gray-600 whitespace-nowrap">
        {filteredUsers} users
      </div>
    </div>
  );
}

/**
 * Quick Filter Buttons for common user filters
 */
export function QuickUserFilters({ 
  activeFilter, 
  onFilterChange, 
  userCounts = {},
  className = '' 
}) {
  const quickFilters = [
    { 
      key: 'all', 
      label: 'All Users', 
      count: userCounts.total || 0 
    },
    { 
      key: USER_ROLES.ADMIN, 
      label: 'Admins', 
      count: userCounts.admin || 0,
      color: 'bg-green-100 text-green-800 hover:bg-green-200'
    },
    { 
      key: USER_ROLES.MODERATOR, 
      label: 'Moderators', 
      count: userCounts.moderator || 0,
      color: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
    },
    { 
      key: USER_ROLES.USER, 
      label: 'Users', 
      count: userCounts.user || 0,
      color: 'bg-blue-100 text-blue-800 hover:bg-blue-200'
    }
  ];

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {quickFilters.map((filter) => (
        <button
          key={filter.key}
          onClick={() => onFilterChange?.(filter.key)}
          className={`
            px-3 py-2 rounded-lg text-sm font-medium transition-colors
            ${activeFilter === filter.key 
              ? filter.color || 'bg-blue-100 text-blue-800' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }
          `}
        >
          {filter.label}
          {filter.count > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-white bg-opacity-50 rounded-full text-xs">
              {filter.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
